import { NavigationItem, OrdersMechanism, ViewKey } from '~/pages/Onboarding/onboarding.constants';
import { OnboardingUserDetail } from '~/pages/Onboarding/onboarding.types';
import {
  OnboardingApi,
  OnboardingInfoState,
  OnboardingOrdersState,
  OnboardingPreferencesState,
  OnboardingProPlanState,
  OnboardingViewBasics,
} from '~/pages/Onboarding/store/onboarding.reducers';
import { createSelector } from 'reselect';
import { getUser } from '~/selectors/user.selectors';
import Address, { Country } from '~/types/Address';
import User from '~/types/User';
import { ApplicationState } from '../../../store';

export const getOnboardingStore = (state: ApplicationState) => state.onboarding;

export const isCurrentlyOnboarding = createSelector(
  getOnboardingStore,
  (onboardingStore): boolean => onboardingStore.active,
);

export const isOnboardingReady = createSelector(
  getOnboardingStore,
  (onboardingStore): boolean => onboardingStore.ready,
);

export const getNavigationSteps = createSelector(
  getOnboardingStore,
  (onboardingStore): NavigationItem[] => onboardingStore.navigation.steps,
);

export const getNavigationCurrentStep = createSelector(
  getOnboardingStore,
  (onboardingStore): number => onboardingStore.navigation.currentStepIndex,
);

export const getNavigationCurrentStepName = createSelector(
  getOnboardingStore,
  (onboardingStore): string =>
    onboardingStore.navigation.steps[onboardingStore.navigation.currentStepIndex].label,
);

export const getNavigationLastCompletedStep = createSelector(
  getOnboardingStore,
  (onboardingStore): number => onboardingStore.navigation.lastCompletedStepIndex,
);

export const getNavigationLastCompletedStepName = createSelector(
  getOnboardingStore,
  (onboardingStore): string =>
    onboardingStore.navigation.steps[onboardingStore.navigation.lastCompletedStepIndex].label,
);

export const getOnboardingInfo = createSelector(
  getOnboardingStore,
  (onboardingStore): OnboardingInfoState => onboardingStore[ViewKey.info],
);

export const getOnboardingCountry = createSelector(
  getOnboardingStore,
  (onboardingStore): Maybe<Country> => onboardingStore[ViewKey.info].country,
);

export const getOnboardingPhone = createSelector(
  getOnboardingStore,
  (onboardingStore): string | null => onboardingStore[ViewKey.info].phone,
);

export const getOnboardingCompany = createSelector(
  getOnboardingStore,
  (onboardingStore): string | null => onboardingStore[ViewKey.info].companyName,
);

export const getOnboardingBusinessType = createSelector(
  getOnboardingStore,
  (onboardingStore): string => onboardingStore[ViewKey.info].businessType,
);

export const getOnboardingBusinessNeed = createSelector(
  getOnboardingStore,
  (onboardingStore): string | undefined => onboardingStore[ViewKey.info].businessNeed,
);

export const getOnboardingPreferences = createSelector(
  getOnboardingStore,
  (onboardingStore): OnboardingPreferencesState => onboardingStore[ViewKey.preferences],
);

export const getOnboardingOrders = createSelector(
  getOnboardingStore,
  (onboardingStore): OnboardingOrdersState => onboardingStore[ViewKey.orders],
);

export const getOrderMechanism = createSelector(
  getOnboardingStore,
  (onboardingStore) => onboardingStore[ViewKey.orders].orderMechanism,
);

export const getOnboardingApi = createSelector(
  getOnboardingStore,
  (onboardingStore): OnboardingApi => onboardingStore[ViewKey.api],
);

export const getOnboardingGettingStarted = createSelector(
  getOnboardingStore,
  (onboardingStore): OnboardingViewBasics => onboardingStore[ViewKey.getting_started],
);

export const getOnboardingShippingDetails = createSelector(
  getOnboardingStore,
  (onboardingStore): OnboardingViewBasics => onboardingStore[ViewKey.shipping_details],
);

export const getOnboardingPlan = createSelector(
  getOnboardingStore,
  (onboardingStore): OnboardingProPlanState => onboardingStore[ViewKey.pro_plan],
);

export const getOnboardingUserInfo = createSelector(
  getUser,
  (user: Maybe<User>): OnboardingUserDetail => {
    return {
      id: user?.id ?? null,
      name: user?.address?.name ?? '',
      email: user?.email ?? null,
      phone: user?.address?.phone ?? null,
      company: user?.address?.company ?? '',
    };
  },
);

export const getOrdersAddressObject = createSelector(
  getOnboardingOrders,
  (onboardingOrders): Address | null => onboardingOrders.addressObject,
);

export const getPreferencesAddressObject = createSelector(
  getOnboardingPreferences,
  (onboardingPreferences): Address | null => onboardingPreferences.addressObject,
);

export const getOnboardingOrderMechanism = createSelector(
  getOnboardingStore,
  (onboardingStore): OrdersMechanism => onboardingStore[ViewKey.orders].orderMechanism,
);

export const getConnectedStoreName = createSelector(
  getOnboardingStore,
  (onboardingStore): string | null | undefined =>
    onboardingStore.connectedStore ? onboardingStore.connectedStore.storePlatform.name : undefined,
);

export const getOnboardingErrors = createSelector(getOnboardingStore, (onboardingStore) => ({
  global: onboardingStore.error,
  [ViewKey.info]: onboardingStore[ViewKey.info].error,
  [ViewKey.preferences]: onboardingStore[ViewKey.preferences].error,
  [ViewKey.orders]: onboardingStore[ViewKey.orders].error,
}));
